import * as React from 'react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import TableViewIcon from '@mui/icons-material/TableView';
import { Badge, ClickAwayListener, Fade, MenuList, Paper, Popper, Tooltip } from '@mui/material';
import { ToolbarButton, useGridApiContext } from '@mui/x-data-grid-premium';
import { UserCustomViewApi } from 'api/patient/UserCustomViewApi';
import NotificationManager from 'common/notifications/NotificationManager';
import { orderBy } from 'lodash';
import { onRequestError } from 'services/handlers';

import { migrateViews } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/CustomViews/CustomViewsServices';
import { TASK_WORKLIST } from 'components/pages/patient-source/TaskWorklist/constants';
import { useCurrentUser } from 'components/root/Container/CurrentUserContainer';

import { SaveCurrentViewButton } from './SaveCurrentViewButton/SaveCurrentViewButton';
import { ViewListItem } from './ViewListItem/ViewListItem';
import { customViewsReducer } from './CustomViewsReducer';

export const CustomViews = ({
  defaultTableState,
  tableStateFromLocalStorage,
  columnGroupingModel,
  columns,
  tableVersion,
  tableName,
  setQuickFilterExpanded
}) => {
  const { personnelIdentifier } = useCurrentUser();
  const apiRef = useGridApiContext();

  const tableStateFromLocalStorageRef = useRef(tableStateFromLocalStorage);
  tableStateFromLocalStorageRef.current = tableStateFromLocalStorage;

  const standardOrderedFields = useMemo(() => defaultTableState.columns.orderedFields, [
    defaultTableState.columns.orderedFields
  ]);
  const [state, dispatch] = useReducer(customViewsReducer, {
    views: {},
    newViewLabel: '',
    isMenuOpened: false,
    menuAnchorEl: null,
    activeViewId: null
  });

  const applyView = useCallback(
    (viewId, views) => {
      if (viewId) {
        try {
          apiRef.current.restoreState(views[viewId].value);
          if (views[viewId].value?.filter?.filterModel.quickFilterValues?.length > 0) {
            setQuickFilterExpanded(true);
          } else {
            setQuickFilterExpanded(false);
          }
        } catch (error) {
          UserCustomViewApi.logViewApplyError(personnelIdentifier, viewId, tableName, error).catch(onRequestError);
          NotificationManager.error(
            'Saved view could not be applied due to table changes. Default view restored. Please resave to keep changes.'
          );
          apiRef.current.restoreState(defaultTableState);
          setQuickFilterExpanded(false);
          dispatch({ type: 'setActiveView', id: null });
        }
      }
    },
    [apiRef, defaultTableState, personnelIdentifier, setQuickFilterExpanded, tableName]
  );

  const applyApiRefStateBasedOnTableType = useCallback(() => {
    if (tableName === TASK_WORKLIST && !!tableStateFromLocalStorageRef.current) {
      apiRef.current.restoreState(tableStateFromLocalStorageRef.current);
    } else {
      apiRef.current.restoreState(defaultTableState);
    }
  }, [apiRef, defaultTableState, tableName]);

  const restoreStateOrApplyViewBasedOnTableType = useCallback(
    (savedActiveViewId, views) => {
      dispatch({
        type: 'setActiveView',
        id: savedActiveViewId
      });
      if (tableName === TASK_WORKLIST && !!tableStateFromLocalStorageRef.current) {
        apiRef.current.restoreState(tableStateFromLocalStorageRef.current);
      } else {
        applyView(savedActiveViewId, views);
      }
    },
    [apiRef, applyView, tableName]
  );

  const loadAllCustomViews = useCallback(() => {
    UserCustomViewApi.getAllUserCustomViews(personnelIdentifier, tableName).then(
      ({ data: { viewsByPageKey, activeViewIds } }) => {
        const workbookViews = viewsByPageKey?.[tableName] ?? {};
        const savedActiveViewId = activeViewIds?.[tableName] ?? null;
        const normalizedViews = Object.fromEntries(
          Object.entries(workbookViews).map(([key, value]) => [key, JSON.parse(value)])
        );

        const oneOfViewsHasNoActualVersion = Object.values(normalizedViews).some(view => view.version !== tableVersion);
        const applyLastActiveView = views => {
          if (!savedActiveViewId) {
            applyApiRefStateBasedOnTableType();
          } else {
            restoreStateOrApplyViewBasedOnTableType(savedActiveViewId, views);
          }
        };

        if (oneOfViewsHasNoActualVersion) {
          const migratedViews = migrateViews(
            normalizedViews,
            defaultTableState,
            standardOrderedFields,
            columnGroupingModel,
            columns,
            tableVersion
          );
          const viewData =
            Object.entries(migratedViews).map(([key, value]) => ({
              viewId: key,
              view: JSON.stringify(value),
              pageKey: tableName
            })) || [];
          UserCustomViewApi.updateCustomViews(personnelIdentifier, tableName, viewData)
            .then(() => {
              dispatch({
                type: 'setViews',
                views: migratedViews
              });
              applyLastActiveView(migratedViews);
            })
            .catch(error => {
              onRequestError(error, {
                customMessage:
                  'Migrated custom views could not be saved. Default view restored. Please resave to keep changes.'
              });
              apiRef.current.restoreState(defaultTableState);
            });
        } else {
          dispatch({
            type: 'setViews',
            views: normalizedViews
          });
          applyLastActiveView(normalizedViews);
        }
      },
      onRequestError
    );
  }, [
    personnelIdentifier,
    tableName,
    tableVersion,
    applyApiRefStateBasedOnTableType,
    restoreStateOrApplyViewBasedOnTableType,
    defaultTableState,
    standardOrderedFields,
    columnGroupingModel,
    columns,
    apiRef
  ]);

  useEffect(() => {
    loadAllCustomViews();
  }, [loadAllCustomViews]);

  const createNewView = () => {
    const newView = apiRef.current.exportState();
    const id = crypto.randomUUID();
    const requestData = {
      viewId: id,
      view: JSON.stringify({ label: state.newViewLabel?.trim(), value: newView, version: tableVersion }),
      pageKey: tableName
    };
    UserCustomViewApi.createCustomView(personnelIdentifier, requestData).then(() => {
      dispatch({
        type: 'createView',
        value: newView,
        id
      });
    }, onRequestError);
  };

  const handleNewViewLabelChange = event => {
    dispatch({ type: 'setNewViewLabel', label: event.target.value });
  };

  const handleCancelNewView = () => {
    dispatch({ type: 'setNewViewLabel', label: '' });
  };

  const handleDeleteView = useCallback(
    viewId => {
      UserCustomViewApi.deleteCustomView(personnelIdentifier, viewId, tableName).then(() => {
        dispatch({ type: 'deleteView', id: viewId });
      }, onRequestError);
    },
    [personnelIdentifier, tableName]
  );

  const handleSetActiveView = viewId => {
    UserCustomViewApi.activateCustomView(personnelIdentifier, viewId, tableName).then(() => {
      dispatch({ type: 'setActiveView', id: viewId });
      applyView(viewId, state.views);
    }, onRequestError);
  };

  const handlePopperAnchorClick = event => {
    dispatch({ type: 'togglePopper', element: event.currentTarget });
    event.stopPropagation();
  };

  const handleClosePopper = () => {
    dispatch({ type: 'closePopper' });
  };

  const isNewViewLabelValid = React.useMemo(() => {
    const trimmedNewViewLabel = state.newViewLabel.trim();
    if (trimmedNewViewLabel.length === 0) {
      return false;
    }

    return Object.values(state.views).every(view => view.label.trim() !== trimmedNewViewLabel);
  }, [state.views, state.newViewLabel]);

  const canBeMenuOpened = state.isMenuOpened && Boolean(state.menuAnchorEl);
  const popperId = canBeMenuOpened ? 'transition-popper' : undefined;

  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      dispatch({ type: 'closePopper' });
    } else if (event.key === 'Escape') {
      dispatch({ type: 'closePopper' });
    }
  };

  const viewsCount = useMemo(() => Object.keys(state.views).length, [state.views]);

  return (
    <>
      <Tooltip title="Custom view">
        <ToolbarButton onClick={handlePopperAnchorClick} disabled={!viewsCount}>
          <Badge badgeContent={viewsCount} color="textSecondary">
            <TableViewIcon fontSize="small" />
          </Badge>
        </ToolbarButton>
      </Tooltip>
      <ClickAwayListener onClickAway={handleClosePopper}>
        <Popper
          id={popperId}
          open={state.isMenuOpened}
          anchorEl={state.menuAnchorEl}
          role={undefined}
          transition
          placement="bottom-end"
          sx={{ zIndex: 'modal' }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper>
                <MenuList
                  autoFocusItem={state.isMenuOpened}
                  id="custom-view-menu"
                  aria-labelledby="custom-view-button"
                  onKeyDown={handleListKeyDown}
                  sx={{ maxHeight: '450px', overflowY: 'auto' }}
                >
                  {orderBy(Object.entries(state.views), ([, view]) => view.label?.toLowerCase(), ['asc']).map(
                    ([viewId, view]) => (
                      <ViewListItem
                        key={viewId}
                        view={view}
                        viewId={viewId}
                        selected={viewId === state.activeViewId}
                        onDelete={handleDeleteView}
                        onSelect={handleSetActiveView}
                      />
                    )
                  )}
                </MenuList>
              </Paper>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
      <SaveCurrentViewButton
        label={state.newViewLabel}
        onLabelChange={handleNewViewLabelChange}
        onSubmit={createNewView}
        onCancel={handleCancelNewView}
        isValid={isNewViewLabelValid}
      />
    </>
  );
};
